import { z } from "zod";

import { StepCardActionType, StepCardContinueVia, StepCardDecisionAsk } from "@/src/models/step-card-action";
import { stepCardBlockViewSchema } from "@/src/models/step-card-block";
import { StepCardState } from "@/src/models/step-card-state";
import { TaskStepStatus } from "@/src/models/task-step";
import { VerificationResult } from "@/src/models/task-verification";

/** Where one workflow step a card owns stands on this task, from its row. */
export enum StepCardWorkflowStepState {
  COMPLETED = "completed",
  FAILED = "failed",
  NOT_NEEDED = "not_needed",
  NOT_RUN = "not_run",
  PROCESSING = "processing",
  RETRYING = "retrying",
}

export const stepCardWorkflowStepViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  state: z.enum(StepCardWorkflowStepState),
});

export type StepCardWorkflowStepView = z.infer<typeof stepCardWorkflowStepViewSchema>;

/** What the page says above the cards when none of them asks ops for anything. */
export enum StepCardRunNotice {
  /** The run is held for ops, but no card can resolve what holds it: engineering's. */
  HELD_UNRESOLVED = "held_unresolved",
  /** The run is going; the cards update as each step completes. */
  RUNNING = "running",
  /** The run is finished or closed, and nothing is left for ops. */
  NOTHING_LEFT = "nothing_left",
}

export enum StepCardNoticeTone {
  INFO = "info",
  WARNING = "warning",
}

export const stepCardNoticeSchema = z.object({ tone: z.enum(StepCardNoticeTone), text: z.string() });

/** One derived step card, as the shared task page renders it. */
export const stepCardViewSchema = z.object({
  key: z.string(),
  title: z.string(),
  /** 1-based position in display order. */
  number: z.number().int().positive(),
  /** What the card shows ops: its badge, colour and controls. */
  state: z.enum(StepCardState),
  /** The earlier card this one is queued behind. */
  blockedBy: z.object({ key: z.string(), title: z.string(), number: z.number().int().positive() }).nullable(),
  verdict: z.nativeEnum(VerificationResult).nullable(),
  /** The Proceed or Forward ops chose on a decision card, shown once its buttons are gone. */
  decidedAction: z
    .enum([StepCardActionType.PROCEED, StepCardActionType.FORWARD])
    .nullable()
    // So a response from a server that predates it still parses during a deploy.
    .default(null),
  /** When a row behind the card last changed; every action sends it back, so a stale click is refused. */
  revision: z.string(),
  workflowStepKeys: z.array(z.string()),
  workflowSteps: z.array(stepCardWorkflowStepViewSchema),
  /** The failed task step to act on, when the card holds one. */
  failedTaskStep: z
    .object({ id: z.string(), stepKey: z.string(), label: z.string(), status: z.nativeEnum(TaskStepStatus) })
    .nullable(),
  failureReason: z.string().nullable(),
  stoppedOnFailure: z.boolean(),
  holdsPendingApproval: z.boolean(),
  /** The run is parked on one of this card's `resumable` steps (awaiting information or review). */
  parked: z.boolean(),
  /** What the pending approval this card holds asks ops (its `instruction`), while the run waits on it. */
  approvalInstruction: z.string().nullable(),
  /** The card's inner blocks, in order; empty when its definition declares none. */
  blocks: z.array(stepCardBlockViewSchema),
  /** What ops can do from this card now; the action endpoint accepts nothing else. */
  actions: z.object({
    types: z.array(z.enum(StepCardActionType)),
    /**
     * What lets the run go on and what it acts on (the approval's or the parked row's id), and, for
     * a resume, why the step's check last failed (a hint; Resume re-checks).
     */
    continue: z
      .object({
        via: z.enum(StepCardContinueVia),
        targetId: z.string(),
        blockedReason: z.string().nullable(),
      })
      .nullable(),
    /** On a decision card offering Proceed / Forward: automation's answer. */
    decision: z
      .object({
        automatedProceed: z.boolean(),
        ask: z.enum(StepCardDecisionAsk).default(StepCardDecisionAsk.PROCEED_FORWARD),
      })
      .nullable()
      // So a response from a server that predates it still parses during a deploy.
      .default(null),
  }),
});

export type StepCardView = z.infer<typeof stepCardViewSchema>;

/** The task's step cards, or null when its customer has none for this workflow. */
export const stepCardsResponseSchema = z.object({
  cards: z.array(stepCardViewSchema).nullable(),
  /** Derived with the cards from the same rows and status, so the two never disagree. */
  runNotice: z.enum(StepCardRunNotice).nullable(),
  /** The workflow's page-level messages whose step is in the state they name. */
  notices: z.array(stepCardNoticeSchema),
});

export type StepCardsResponse = z.infer<typeof stepCardsResponseSchema>;
